/**
 * Flow formulas: how the flow of a section is extrapolated from one or more
 * gauges. Shared by the gauge Cloud Function (dataExtractFunctions.js) and the
 * admin Station tab (src/utils/flowFormulas.js), so the comparison chart of the
 * admin shows exactly what the function will write.
 *
 * A section keeps several formulas in its extrapolation document; one of them
 * (primary_formula_id) gives the public level, the others can be drawn on the
 * public chart (show_on_chart) to compare.
 *
 *   flow(t) = offset + sum of (sign x factor x gauge flow at t - lag_hours)
 *
 * Each term uses factor_up while its own gauge is rising (or stable) over the
 * last hour, factor_down while it is falling. The time grid is the readings of
 * the first term's gauge. Gauge times are local times without a time zone
 * (CEHQ, Hydro-Québec); they are all read as UTC, which keeps the differences
 * right as long as both sources use the same clock.
 */

export const ACCURACY_LEVELS = ["high", "medium", "low", "unknown"];

export const HOUR_MS = 60 * 60 * 1000;
// Trend of a gauge: difference with its flow one hour earlier
export const TREND_WINDOW_MS = HOUR_MS;
// Two readings further apart than this are not interpolated between
const MAX_INTERPOLATION_GAP_MS = 6 * HOUR_MS;
// A gauge that publishes later than the first one keeps its last reading
// this long
const MAX_HOLD_MS = 2 * HOUR_MS;

/**
 * @param {*} value
 * @param {number} fallback
 * @return {number}
 */
function toNumber(value, fallback) {
  if (value === null || value === undefined || value === "") return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

/**
 * @param {string} date "YYYY-MM-DD" (or with slashes)
 * @param {string} hour "HH:MM" (seconds ignored)
 * @return {number} Milliseconds, or NaN.
 */
export function parseReadingTime(date, hour) {
  const dateText = String(date || "").trim();
  const dateMatch = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/.exec(dateText);
  const hourMatch = /^(\d{1,2}):(\d{2})/.exec(String(hour || "").trim());
  if (!dateMatch || !hourMatch) return NaN;
  return Date.UTC(
      Number(dateMatch[1]),
      Number(dateMatch[2]) - 1,
      Number(dateMatch[3]),
      Number(hourMatch[1]),
      Number(hourMatch[2]),
  );
}

/**
 * Readings of a gauge, oldest first, without the missing flows.
 *
 * @param {(string|Object)} dataHistory data_history of a gauge (JSON string)
 *     or the scraper result: { level: [{ date, hour, flow }] }, newest first.
 * @return {Array<{t: number, flow: number, date: string, hour: string}>}
 */
export function parseGaugeReadings(dataHistory) {
  let data = dataHistory;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (error) {
      return [];
    }
  }
  const levels = data && Array.isArray(data.level) ? data.level : [];
  return levels
      .map((entry) => ({
        t: parseReadingTime(entry && entry.date, entry && entry.hour),
        flow: toNumber(entry && entry.flow, NaN),
        date: entry && entry.date,
        hour: entry && entry.hour,
      }))
      .filter((reading) => {
        return Number.isFinite(reading.t) && Number.isFinite(reading.flow);
      })
      .sort((a, b) => a.t - b.t);
}

/**
 * Flow of a gauge at a time, interpolated between the readings around it.
 *
 * @param {Array<{t: number, flow: number}>} readings Oldest first.
 * @param {number} t
 * @return {?number} null when there is no reading close enough.
 */
export function flowAt(readings, t) {
  if (!readings.length || !Number.isFinite(t)) return null;
  const last = readings[readings.length - 1];
  if (t >= last.t) {
    return t - last.t <= MAX_HOLD_MS ? last.flow : null;
  }
  if (t < readings[0].t) return null;

  // Last reading at or before t
  let low = 0;
  let high = readings.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (readings[middle].t <= t) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }
  const before = readings[low];
  if (before.t === t) return before.flow;
  const after = readings[low + 1];
  if (after.t - before.t > MAX_INTERPOLATION_GAP_MS) return null;
  const ratio = (t - before.t) / (after.t - before.t);
  return before.flow + (after.flow - before.flow) * ratio;
}

/**
 * @param {*} value
 * @return {string} One of ACCURACY_LEVELS.
 */
function normalizeAccuracy(value) {
  return ACCURACY_LEVELS.includes(value) ? value : "unknown";
}

/**
 * @param {Object} term
 * @return {Object}
 */
export function normalizeTerm(term = {}) {
  const factorUp = toNumber(term.factor_up, 1);
  return {
    gauge_id: term.gauge_id || "",
    gauge_station_name: term.gauge_station_name || "",
    type: term.type || "",
    operator: term.operator === "-" ? "-" : "+",
    factor_up: factorUp,
    // Without a falling factor, the term uses the same factor both ways
    factor_down: toNumber(term.factor_down, factorUp),
    lag_hours: Math.max(0, toNumber(term.lag_hours, 0)),
  };
}

/**
 * @param {Object} formula
 * @param {number} index Position, for the default id and title.
 * @return {Object}
 */
export function normalizeFormula(formula = {}, index = 0) {
  return {
    id: formula.id || `formula-${index + 1}`,
    title: formula.title || `Calcul ${index + 1}`,
    accuracy: normalizeAccuracy(formula.accuracy),
    show_on_chart: Boolean(formula.show_on_chart),
    offset: toNumber(formula.offset, 0),
    terms: (Array.isArray(formula.terms) ? formula.terms : [])
        .map(normalizeTerm),
  };
}

/**
 * Formulas of an extrapolation document. Documents saved before formulas
 * existed (one gauge, weighting_up / weighting_down) become one formula.
 *
 * @param {Object} extrapolation
 * @return {{formulas: Array<Object>, primaryFormulaId: string}}
 */
export function getExtrapolationFormulas(extrapolation = {}) {
  let formulas = Array.isArray(extrapolation.formulas) ?
    extrapolation.formulas.map(normalizeFormula) :
    [];

  if (!formulas.length && extrapolation.gauge_id) {
    formulas = [
      normalizeFormula({
        id: "formula-1",
        title: "Calcul principal",
        accuracy: "unknown",
        terms: [
          {
            gauge_id: extrapolation.gauge_id,
            gauge_station_name: extrapolation.gauge_station_name,
            type: extrapolation.type,
            operator: "+",
            factor_up: extrapolation.weighting_up,
            factor_down: extrapolation.weighting_down,
            lag_hours: 0,
          },
        ],
      }),
    ];
  }

  const primary = formulas.find((formula) => {
    return formula.id === extrapolation.primary_formula_id;
  }) || formulas[0];
  return {formulas, primaryFormulaId: primary ? primary.id : ""};
}

/**
 * Every gauge used by a list of formulas.
 *
 * @param {Array<Object>} formulas
 * @return {Array<string>}
 */
export function getFormulaGaugeIds(formulas = []) {
  const ids = new Set();
  formulas.forEach((formula) => {
    (formula.terms || []).forEach((term) => {
      if (term.gauge_id) ids.add(term.gauge_id);
    });
  });
  return [...ids];
}

/**
 * Fields written on the extrapolation document. The legacy single-gauge
 * fields mirror the first term of the primary formula, so older readers
 * (gauge page, river counts) keep working.
 *
 * @param {Array<Object>} formulas
 * @param {string} primaryFormulaId
 * @return {Object}
 */
export function buildExtrapolationDoc(formulas, primaryFormulaId) {
  const normalized = formulas.map(normalizeFormula);
  const primary = normalized.find((formula) => {
    return formula.id === primaryFormulaId;
  }) || normalized[0];
  const firstTerm = primary && primary.terms[0] ? primary.terms[0] : null;
  return {
    formulas: normalized,
    primary_formula_id: primary ? primary.id : "",
    gauge_ids: getFormulaGaugeIds(normalized),
    gauge_id: firstTerm ? firstTerm.gauge_id : "",
    gauge_station_name: firstTerm ? firstTerm.gauge_station_name : "",
    type: firstTerm ? firstTerm.type : "",
    weighting_up: firstTerm ? firstTerm.factor_up : null,
    weighting_down: firstTerm ? firstTerm.factor_down : null,
  };
}

/**
 * @param {number} value
 * @return {string}
 */
function format2(value) {
  return (Math.round(value * 100) / 100).toFixed(2);
}

/**
 * Flow history of a section for one formula.
 *
 * @param {Object} formula
 * @param {Object<string, Array>} readingsByGauge parseGaugeReadings() of each
 *     gauge, by gauge id.
 * @return {{level: Array<Object>, missingGaugeIds: Array<string>}} level is
 *     newest first, in the format of sectionsLevelHistory: { date, hour,
 *     level, flow, trend } with flow and trend as strings of 2 decimals.
 */
export function evaluateFormula(formula, readingsByGauge = {}) {
  const normalized = normalizeFormula(formula);
  const terms = normalized.terms.filter((term) => term.gauge_id);
  const missingGaugeIds = terms
      .map((term) => term.gauge_id)
      .filter((id) => !(readingsByGauge[id] && readingsByGauge[id].length));
  if (!terms.length || missingGaugeIds.length) {
    return {level: [], missingGaugeIds: [...new Set(missingGaugeIds)]};
  }

  const grid = readingsByGauge[terms[0].gauge_id];
  const level = [];
  grid.forEach((gridReading) => {
    let flow = normalized.offset;
    let trend = 0;
    for (const term of terms) {
      const readings = readingsByGauge[term.gauge_id];
      const t = gridReading.t - term.lag_hours * HOUR_MS;
      const value = flowAt(readings, t);
      if (value === null) return;
      const previous = flowAt(readings, t - TREND_WINDOW_MS);
      const termTrend = previous === null ? 0 : value - previous;
      const factor = termTrend >= 0 ? term.factor_up : term.factor_down;
      const sign = term.operator === "-" ? -1 : 1;
      flow += sign * factor * value;
      trend += sign * factor * termTrend;
    }
    level.push({
      date: gridReading.date,
      hour: gridReading.hour,
      level: "",
      // A difference of gauges can go below zero; a river can't
      flow: format2(Math.max(0, flow)),
      trend: format2(trend),
    });
  });

  return {level: level.reverse(), missingGaugeIds: []};
}
